import React, { useMemo, useState } from 'react';
import {
  Alert,
  Button,
  Checkbox,
  InputNumber,
  Modal,
  Radio,
  Select,
  Space,
  Steps,
  Table,
  Typography,
  message
} from 'antd';
import type { RouteViewModel } from '../../../types/routeViewModel';
import type { ApplicationCargo } from '../../../types';
import {
  useAddRouteCargoSegmentMutation,
  useGetAvailableCargoQuery
} from '../../../store/api/routesApi';
import { useGetAllWarehousesQuery } from '../../../store/api/warehousesApi';
import { useGetApplicationsQuery } from '../../../store/api/applicationsApi';
import { MutationReasonField } from '../../mutationWorkspace/MutationReasonField';
import { isSequentialStopsReadModeEnabled } from '../../sequentialStops/sequentialStopsReadFlags';
import { sortStopsByPosition } from '../../sequentialStops/sequentialStopsOrdering';
import { stopLabel, viewStopToDraft } from '../../mutationWorkspace/mutationStopHelpers';
import type { CorrectionContext } from '../hooks/useCorrectionContext';
import { mapRejectMessage } from '../../mutationWorkspace/buildMutationPolicyPreview';

const { Text } = Typography;

type Props = {
  routeId: number;
  view: RouteViewModel;
  ctx: CorrectionContext;
  open: boolean;
  onClose: () => void;
  onSuccess: () => void;
};

type SelectionRow = {
  cargoId: number;
  quantity: number;
  unloadMode: 'existing' | 'new';
  existingStopId?: number;
};

export function AddCargoSegmentWizard({ routeId, view, ctx, open, onClose, onSuccess }: Props) {
  const [step, setStep] = useState(0);
  const [sourceType, setSourceType] = useState<'application' | 'warehouse'>('application');
  const [sourceId, setSourceId] = useState<number | undefined>();
  const [insertAfterPosition, setInsertAfterPosition] = useState<number | 'end'>('end');
  const [selections, setSelections] = useState<Record<number, SelectionRow>>({});
  const [mutationReason, setMutationReason] = useState<string | null>(null);
  const [confirmLive, setConfirmLive] = useState(false);

  const { data: warehouses = [] } = useGetAllWarehousesQuery(undefined, { skip: !open });
  const { data: applicationsData } = useGetApplicationsQuery({ page: 1, limit: 200 }, { skip: !open });
  const applications = applicationsData?.applications ?? [];

  const { data: availableCargo = [], isFetching: cargoLoading } = useGetAvailableCargoQuery(
    {
      loadingPointType: sourceType === 'application' ? 'application' : 'warehouse',
      loadingPointId: sourceId,
      excludeRouteId: routeId
    },
    { skip: !open || sourceId == null }
  );

  const [addSegment, { isLoading: saving }] = useAddRouteCargoSegmentMutation();

  const futureUnloadStops = useMemo(() => {
    const stops = view.structure?.stops ?? [];
    const ordered = isSequentialStopsReadModeEnabled()
      ? sortStopsByPosition(stops)
      : [...stops].sort((a, b) => Number(a.position) - Number(b.position));

    return ordered
      .map((s, i) => viewStopToDraft(s, i))
      .filter(
        (s) =>
          s.type === 'unloading' &&
          s.id != null &&
          ctx.futureEditableStopIds.includes(Number(s.id))
      );
  }, [view.structure?.stops, ctx.futureEditableStopIds]);

  const orderedStops = useMemo(() => {
    const stops = view.structure?.stops ?? [];
    return isSequentialStopsReadModeEnabled()
      ? sortStopsByPosition(stops)
      : [...stops].sort((a, b) => Number(a.position) - Number(b.position));
  }, [view.structure?.stops]);

  const reset = () => {
    setStep(0);
    setSourceType('application');
    setSourceId(undefined);
    setInsertAfterPosition('end');
    setSelections({});
    setMutationReason(null);
    setConfirmLive(false);
  };

  const handleClose = () => {
    reset();
    onClose();
  };

  const selectedRows = Object.values(selections).filter((s) => s.quantity > 0);

  const handleSubmit = async () => {
    if (sourceId == null || !mutationReason || selectedRows.length === 0) {
      message.error('Заполните все обязательные поля');
      return;
    }

    try {
      await addSegment({
        routeId,
        expectedUpdatedAt: ctx.routeUpdatedAt,
        mutationReason,
        confirmationProvided: confirmLive || undefined,
        insertAfterPosition: insertAfterPosition === 'end' ? null : insertAfterPosition,
        source: { type: sourceType, id: sourceId },
        selections: selectedRows.map((row) => {
          const cargo = availableCargo.find((c) => c.id === row.cargoId);
          if (row.unloadMode === 'existing' && row.existingStopId) {
            return {
              cargoId: row.cargoId,
              quantity: row.quantity,
              unloading: { mode: 'existing' as const, stopId: row.existingStopId }
            };
          }
          return {
            cargoId: row.cargoId,
            quantity: row.quantity,
            unloading: {
              mode: 'new' as const,
              draft: {
                pointType: 'application' as const,
                pointId: cargo?.applicationId ?? sourceId,
                address: cargo?.application?.unloadingAddress ?? '—',
                city: cargo?.application?.unloadingCity ?? undefined
              }
            }
          };
        })
      }).unwrap();
      message.success('Сегмент груза добавлен');
      handleClose();
      onSuccess();
    } catch (e: unknown) {
      const err = e as { data?: unknown; status?: number };
      const msg =
        err.status === 409
          ? 'Маршрут был изменён. Обновите карточку и повторите.'
          : mapRejectMessage(err.data) ?? 'Не удалось добавить сегмент';
      message.error(msg);
    }
  };

  const cargoColumns = [
    { title: 'Груз', dataIndex: 'name', key: 'name' },
    {
      title: 'Доступно',
      key: 'avail',
      render: (_: unknown, row: ApplicationCargo) => row.availableQuantity ?? row.quantity
    },
    {
      title: 'Кол-во',
      key: 'qty',
      render: (_: unknown, row: ApplicationCargo) => (
        <InputNumber
          min={0}
          max={row.availableQuantity ?? row.quantity}
          value={selections[row.id]?.quantity ?? 0}
          onChange={(v) =>
            setSelections((prev) => ({
              ...prev,
              [row.id]: {
                cargoId: row.id,
                quantity: Number(v) || 0,
                unloadMode: prev[row.id]?.unloadMode ?? 'new',
                existingStopId: prev[row.id]?.existingStopId
              }
            }))
          }
        />
      )
    },
    {
      title: 'Выгрузка',
      key: 'unload',
      render: (_: unknown, row: ApplicationCargo) => {
        const sel = selections[row.id];
        if (!sel?.quantity) return null;
        return (
          <Select
            style={{ minWidth: 180 }}
            value={sel.unloadMode === 'existing' ? `existing:${sel.existingStopId}` : 'new'}
            onChange={(v) => {
              const isExisting = String(v).startsWith('existing:');
              setSelections((prev) => ({
                ...prev,
                [row.id]: {
                  ...prev[row.id],
                  cargoId: row.id,
                  quantity: prev[row.id]?.quantity ?? 0,
                  unloadMode: isExisting ? 'existing' : 'new',
                  existingStopId: isExisting ? Number(String(v).split(':')[1]) : undefined
                }
              }));
            }}
            options={[
              { value: 'new', label: 'Новая точка выгрузки' },
              ...futureUnloadStops.map((s) => ({
                value: `existing:${s.id}`,
                label: stopLabel(s)
              }))
            ]}
          />
        );
      }
    }
  ];

  return (
    <Modal
      title="Добавить сегмент груза"
      open={open}
      onCancel={handleClose}
      width={720}
      footer={
        <Space>
          <Button onClick={handleClose}>Отмена</Button>
          {step > 0 && <Button onClick={() => setStep((s) => s - 1)}>Назад</Button>}
          {step < 3 ? (
            <Button
              type="primary"
              disabled={
                (step === 0 && sourceId == null) ||
                (step === 1 && availableCargo.length === 0) ||
                (step === 2 && selectedRows.length === 0)
              }
              onClick={() => setStep((s) => s + 1)}
            >
              Далее
            </Button>
          ) : (
            <Button
              type="primary"
              loading={saving}
              disabled={!mutationReason || selectedRows.length === 0}
              onClick={handleSubmit}
            >
              Добавить
            </Button>
          )}
        </Space>
      }
    >
      <Steps
        current={step}
        size="small"
        style={{ marginBottom: 24 }}
        items={[
          { title: 'Источник' },
          { title: 'Грузы' },
          { title: 'Позиция' },
          { title: 'Подтверждение' }
        ]}
      />

      {step === 0 && (
        <Space direction="vertical" style={{ width: '100%' }}>
          <Radio.Group
            value={sourceType}
            onChange={(e) => {
              setSourceType(e.target.value);
              setSourceId(undefined);
              setSelections({});
            }}
          >
            <Radio.Button value="application">Заявка</Radio.Button>
            <Radio.Button value="warehouse">Склад</Radio.Button>
          </Radio.Group>
          <Select
            showSearch
            placeholder={sourceType === 'application' ? 'Выберите заявку' : 'Выберите склад'}
            style={{ width: '100%' }}
            value={sourceId}
            onChange={setSourceId}
            options={
              sourceType === 'application'
                ? applications.map((a) => ({
                    value: a.id,
                    label: a.internalNumber ?? `#${a.id}`
                  }))
                : warehouses.map((w) => ({
                    value: w.id,
                    label: w.name ?? `#${w.id}`
                  }))
            }
          />
        </Space>
      )}

      {step === 1 && (
        <>
          {cargoLoading ? (
            <Text type="secondary">Загрузка доступных грузов…</Text>
          ) : availableCargo.length === 0 ? (
            <Alert type="warning" message="Нет доступных грузов для выбранного источника" />
          ) : (
            <Table
              size="small"
              rowKey="id"
              pagination={false}
              dataSource={availableCargo}
              columns={cargoColumns}
            />
          )}
        </>
      )}

      {step === 2 && (
        <Radio.Group
          value={insertAfterPosition}
          onChange={(e) => setInsertAfterPosition(e.target.value)}
        >
          <Space direction="vertical">
            <Radio value="end">В конец маршрута</Radio>
            {orderedStops.map((s) => (
              <Radio key={String(s.id)} value={Number(s.position)}>
                После: {String(s.city ?? s.address ?? `#${s.id}`)}
              </Radio>
            ))}
          </Space>
        </Radio.Group>
      )}

      {step === 3 && (
        <Space direction="vertical" style={{ width: '100%' }}>
          <Text>Выбрано позиций: {selectedRows.length}</Text>
          <MutationReasonField value={mutationReason} onChange={setMutationReason} disabled={saving} />
          {ctx.correction.executionLock.inTransit && (
            <Checkbox checked={confirmLive} onChange={(e) => setConfirmLive(e.target.checked)}>
              Подтверждаю изменение маршрута в пути
            </Checkbox>
          )}
        </Space>
      )}
    </Modal>
  );
}
